import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

export interface PlateLetterMapping {
  letter: string;
  mappedLetters?: string[];
}

@Component({
  selector: 'app-plate-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './plate-preview.component.html',
  styleUrl: './plate-preview.component.scss'
})
export class PlatePreviewComponent {
  @Input() digits = '';
  @Input() letters = '';
  @Input() mappingLetters: PlateLetterMapping[] = [];
  @Input() countryEnglishName = '';
  @Input() countryAlt = '';
  @Input() ariaLabel = '';
  @Input() compactOnMobile = false;

  get originalLetters(): string[] {
    return this.splitPlateLetters(this.letters);
  }

  get mappedDigits(): string {
    return this.convertToArabicNumbers(this.digits);
  }

  get mappedLetters(): string[] {
    const letters = this.originalLetters;

    if (!this.mappingLetters.length) {
      return letters;
    }

    return letters.map((letter) => {
      if (this.isArabicLetter(letter)) {
        const mapping = this.mappingLetters.find((item) =>
          item.mappedLetters?.some((ml) => ml.toLowerCase() === letter.toLowerCase())
        );
        return mapping?.letter || letter;
      }

      const mapping = this.mappingLetters.find((item) => item.letter.toLowerCase() === letter.toLowerCase());
      return mapping?.mappedLetters?.[0] || letter;
    });
  }

  get countryLogo(): string {
    const logos: Record<string, string> = {
      'saudi arabia': 'assets/flags/flag-ksa-icon.svg',
      egypt: 'assets/flags/flag-egypt-icon.svg'
    };
    return logos[this.normalizedCountryName] || 'assets/flags/global.svg';
  }

  get countryCode(): string {
    const codes: Record<string, string> = {
      'saudi arabia': 'KSA',
      egypt: 'EG'
    };
    const name = (this.countryEnglishName || '').trim();
    return codes[this.normalizedCountryName] || (name ? name.substring(0, 3).toUpperCase() : 'KSA');
  }

  hasArabicPlateLetters(letters: string[]): boolean {
    return letters.some((letter) => this.isArabicLetter(letter));
  }

  private get normalizedCountryName(): string {
    return (this.countryEnglishName || '').toLowerCase();
  }

  private splitPlateLetters(letters: string): string[] {
    const normalizedLetters = (letters || '').trim();
    if (!normalizedLetters) {
      return [];
    }

    if (/\s/.test(normalizedLetters)) {
      return normalizedLetters.split(/\s+/).filter(Boolean);
    }

    return Array.from(normalizedLetters);
  }

  private convertToArabicNumbers(numbers: string): string {
    const arabicNumbers = ['\u0660', '\u0661', '\u0662', '\u0663', '\u0664', '\u0665', '\u0666', '\u0667', '\u0668', '\u0669'];
    return (numbers || '').replace(/\d/g, (digit) => arabicNumbers[Number(digit)]);
  }

  isArabicLetter(value: string): boolean {
    return /[\u0600-\u06FF]/.test(value || '');
  }
}
